import PropTypes from 'prop-types';
import React from 'react';
import { defineMessages, injectIntl, intlShape } from 'react-intl';
import flattenDeep from 'lodash/flattenDeep';
import ModalButton from 'brastrap/common/modal/ModalButton';
import FacetToken from 'brastrap/promotional/facet-token/FacetToken';
import FacetMenu from 'brastrap/containers/results-facets/ResultsFacetsMenuContainer.jsx';
import FacetList from './ResultsFacetsList';
import FacetContainer from './ResultsFacetsContainer';

const messages = defineMessages({
  label: { id: 'results-facets.label', defaultMessage: 'Filters' },
  title: { id: 'results-facets.title', defaultMessage: 'Apply Filters' },
});

/**
 * Find and format all selected values for a facet.
 * If there is only one value to select don't bother with the facet
 *
 * @param {Array} facets
 * @returns {Array}
 */
const findSelected = facets =>
  flattenDeep(
    facets.map(facet => {
      if (facet.values.length <= 1) return [];

      return facet.values
        .map(option => ({
          ...option,
          facetId: facet.id,
        }))
        .filter(option => option.selected);
    })
  );

/**
 * @param {Object} props
 * @return {XML}
 * @constructor
 */
const ResultsFacets = props => {
  const {
    facets,
    intl: { formatMessage },
    isOpen,
    modalId,
    modalTarget,
    onChange,
    onModalClose,
    onModalOpen,
    showInModal,
  } = props;

  const tokens = findSelected(facets).map(option => (
    <FacetToken
      key={`${option.facetId}-${option.id}`}
      label={option.name}
      onRemove={() => onChange(false, option.id, option.facetId)}
    />
  ));

  const simpleFacets = facets.filter(f => !f.enhanced);
  const enhancedFacets = facets.filter(f => f.enhanced);
  const displayFacets = simpleFacets.some(facet => facet.values.length > 1);

  return (
    <div className="c-results-facets">
      {enhancedFacets.length > 0 && (
        <FacetContainer {...{ facets: enhancedFacets, onChange }} />
      )}
      {displayFacets &&
        (showInModal ? (
          <ModalButton
            button={{ label: formatMessage(messages.label) }}
            modal={{ title: formatMessage(messages.title) }}
            onModalOpen={onModalOpen}
            onModalClose={onModalClose}
            modalId={modalId}
            modalTarget={modalTarget}
            isOpen={isOpen}
          >
            <FacetList {...{ facets: simpleFacets, onChange }} />
          </ModalButton>
        ) : (
          <FacetMenu {...{ facets: simpleFacets, onChange }} />
        ))}
      {tokens}
    </div>
  );
};

ResultsFacets.propTypes = {
  facets: PropTypes.array,
  intl: intlShape,
  isOpen: PropTypes.bool,
  modalId: PropTypes.string.isRequired,
  modalTarget: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onModalOpen: PropTypes.func,
  onModalClose: PropTypes.func,
  showInModal: PropTypes.bool,
};

ResultsFacets.defaultProps = {
  facets: [],
  onChange: () => {},
  onModalOpen: () => {},
  onModalClose: () => {},
};

export default injectIntl(ResultsFacets);
